import { Box, Theme, Collapse, IconButton, TableRow, TableCell, SxProps } from '@mui/material';
import { Delete, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useCallback, MouseEvent as ReactMouseEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ProductCard } from 'quote-detail/product-card';
import { NumberText } from 'quote-detail/number-text';
import { useQuoteState } from 'shared/contexts';
import { Brands, Product } from 'shared/types';
import { discountRecommendationBrands, listPriceBrands } from 'shared/constants';

const productSummaryContainerStyles: SxProps<Theme> = (theme: Theme) => ({
  '& .MuiTableCell-body': {
    '&:first-of-type': {
      paddingLeft: theme.spacing(5),
    },
  },
});
const productCardContainerStyles: SxProps<Theme> = (theme: Theme) => ({
  padding: 0,
  backgroundColor: theme.palette.background.paper,
});

interface ProductSummaryProps {
  product: Product & Record<'id', string>;
  index: number;
  remove: (index?: number | number[] | undefined) => void;
}
export function ProductSummary({ product, index, remove }: ProductSummaryProps) {
  const { editMode } = useQuoteState();
  const [open, setOpen] = useState(false);
  const { brand } = useParams<{ brand: Brands }>();
  const isPartsVsEquipmentAvailable = ['lewis', 'inscoprocess'].includes(brand);
  const isDiscountBasedBrand: boolean = discountRecommendationBrands.includes(brand);
  const isListPriceBrand: boolean = listPriceBrands.includes(brand);
  const isCBEbrand: boolean = brand === 'cbe';
  const isNianticBrand: boolean = brand === 'nianticseal';

  const removeProduct = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      remove(index);
    },
    [remove, index]
  );

  const toggleRow = () => {
    setOpen(previousToggledValue => !previousToggledValue);
  };

  return (
    <>
      <TableRow sx={productSummaryContainerStyles}>
        <TableCell align='left'>{product.name}</TableCell>
        <TableCell align='left'>{product.itemId}</TableCell>
        <TableCell align='left'>{product.productType}</TableCell>
        <TableCell align='left'>{product.vendorName}</TableCell>
        {isNianticBrand && <TableCell align='left'>{product.customerProductNumber}</TableCell>}
        {isCBEbrand && <TableCell align='left'>{product.freightadder}</TableCell>}
        {isCBEbrand && <TableCell align='left'>{product.exchangerate}</TableCell>}
        <TableCell align='left'>
          <NumberText
            prefix='$'
            text={product.lastCostPaid}
            decimalScale={isNianticBrand ? 4 : 2}
            fixedDecimalScale={isNianticBrand}
          />
        </TableCell>
        {isCBEbrand && <TableCell align='left'>{product.currencytype}</TableCell>}
        {(isDiscountBasedBrand || isListPriceBrand) && (
          <TableCell align='left'>
            <NumberText prefix='$' text={product.listPrice} />
          </TableCell>
        )}
        <TableCell align='left'>{product.quantity}</TableCell>
        <TableCell align='left'>
          <NumberText prefix='$' decimalScale={isNianticBrand ? 4 : 2} fixedDecimalScale text={product.revisedPrice} />
        </TableCell>
        <TableCell align='left'>
          <NumberText prefix='$' decimalScale={isNianticBrand ? 4 : 2} fixedDecimalScale text={product.extendedPrice} />
        </TableCell>
        {isPartsVsEquipmentAvailable && <TableCell align='left'>{product.MROvsCapital || 'NOT APPLICABLE'}</TableCell>}
        {isCBEbrand && <TableCell align='left'>{product.factorSheetName || 'NOT AVAILABLE'}</TableCell>}
        <TableCell align='right'>
          <IconButton aria-label='Vendor' onClick={toggleRow}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
          {editMode && (
            <IconButton onClick={removeProduct}>
              <Delete />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={productCardContainerStyles} colSpan={12}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box margin={3}>
              <ProductCard editMode={editMode} index={index} product={product} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
